const CONSENT_KEY = 'guiri_analytics_consent';

export type ConsentStatus = 'granted' | 'denied';

export function getConsentStatus(): ConsentStatus | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    if (v === 'granted' || v === 'denied') return v;
  } catch {
    // localStorage unavailable (private browsing restrictions)
  }
  return null;
}

export function setConsentStatus(status: ConsentStatus): void {
  try {
    localStorage.setItem(CONSENT_KEY, status);
  } catch {
    // noop
  }
}

/**
 * Initialise PostHog analytics. Call this after the user grants consent, or
 * immediately on app boot when stored consent is 'granted'.
 *
 * All call-sites (posthogCapture.ts, usePostHog.ts) already guard on
 * window.posthog being undefined, so no-op until this runs is safe.
 */
export function initPostHog(): void {
  if (typeof window === 'undefined') return;
  if (window.posthog?.__loaded) return;

  // Inline the standard PostHog snippet so we don't need the posthog-js npm
  // package. Extracted verbatim from the PostHog docs — identical to what was
  // previously in index.html, just deferred until consent is granted.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (function (t: any, e: any) {
    if (e.__SV || (window.posthog && window.posthog.__loaded)) return;
    window.posthog = e as typeof window.posthog;
    e._i = [];
    e.init = function (i: string, s: any, a: any) {
      function g(t: any, e: string) {
        const o = e.split('.');
        if (o.length === 2) { t = t[o[0]]; e = o[1]; }
        t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); };
      }
      const p = t.createElement('script');
      p.type = 'text/javascript';
      p.crossOrigin = 'anonymous';
      p.async = true;
      p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
      const r = t.getElementsByTagName('script')[0];
      r.parentNode.insertBefore(p, r);
      const u: any = a !== undefined ? (e[a] = []) : e;
      u.people = u.people || [];
      u.toString = function (t: boolean) {
        let e = 'posthog';
        if (a !== 'posthog') e += '.' + a;
        if (!t) e += ' (stub)';
        return e;
      };
      u.people.toString = function () { return u.toString(1) + '.people (stub)'; };
      'init Br Vr ci Wr Jr zr qr capture Ni calculateEventProperties Xr register register_once register_for_session unregister unregister_for_session es getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey cancelPendingSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Zr Yr createPersonProfile ts Nr rs opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing Kr debug O Qr getPageViewId captureTraceFeedback captureTraceMetric Dr'
        .split(' ')
        .forEach((m) => g(u, m));
      e._i.push([i, s, a]);
    };
    e.__SV = 1;
  })(document, window.posthog || []);
  /* eslint-enable @typescript-eslint/no-explicit-any */

  window.posthog!.init('phc_y27tYgwhSHExjil6YvrmzRE2DaYVEKaWIsJGWhYcR8L', {
    api_host: 'https://us.i.posthog.com',
    defaults: '2025-11-30',
    person_profiles: 'identified_only',
    disable_session_recording: true,
  } as Parameters<NonNullable<typeof window.posthog>['init']>[1]);
}
